import { Box, Card, CardContent, Stack, Typography } from "@mui/material";
import type { ReactNode } from "react";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import { tokens } from "../theme";

/** A trend delta with up/down arrow; `goodWhenDown` flips the success colour. */
export function Delta({
  value,
  goodWhenDown = false,
}: {
  value: number;
  goodWhenDown?: boolean;
}) {
  const up = value >= 0;
  const good = goodWhenDown ? !up : up;
  const color = good ? tokens.success : tokens.error;
  return (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.25 }}>
      {up ? (
        <TrendingUpIcon sx={{ fontSize: 16, color }} />
      ) : (
        <TrendingDownIcon sx={{ fontSize: 16, color }} />
      )}
      <Typography
        component="span"
        sx={{ fontSize: 13, fontWeight: 600, color }}
      >
        {up ? "+" : ""}
        {value}%
      </Typography>
    </Box>
  );
}

export interface KpiCardProps {
  label: string;
  value: string;
  /**
   * Optional supporting line under the value. A node rather than a string so a
   * card can set part of that line apart (a tooltip, a tinted figure) when it
   * carries more than one number.
   */
  sub?: ReactNode;
  /** Optional trend delta (percent). Omit when there's no comparison period. */
  delta?: number;
  goodWhenDown?: boolean;
  icon: ReactNode;
  /** Override the icon/accent tint (defaults to the brand periwinkle). */
  tint?: string;
  accent?: string;
}

/** Compact metric card: tinted icon, label, big value, optional delta + sub. */
export function KpiCard({
  label,
  value,
  sub,
  delta,
  goodWhenDown,
  icon,
  tint = tokens.brandTint,
  accent = tokens.brand,
}: KpiCardProps) {
  const hasFooter = delta !== undefined || !!sub;
  return (
    <Card
      sx={{
        height: "100%",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: "0 8px 20px rgba(15,23,42,0.08)",
        },
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2.5,
              display: "grid",
              placeItems: "center",
              bgcolor: tint,
              color: accent,
            }}
          >
            {icon}
          </Box>
          <Typography variant="body2" color="text.secondary">
            {label}
          </Typography>
        </Stack>
        <Typography
          sx={{
            mt: 2,
            fontSize: 30,
            fontWeight: 700,
            letterSpacing: "-0.02em",
          }}
        >
          {value}
        </Typography>
        {hasFooter && (
          <Stack
            direction="row"
            spacing={0.75}
            alignItems="center"
            sx={{ mt: 1 }}
          >
            {delta !== undefined && (
              <Delta value={delta} goodWhenDown={goodWhenDown} />
            )}
            {sub && <Typography variant="caption">{sub}</Typography>}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}

export default KpiCard;
